import { useState, type JSX } from 'react';
import { api } from '../net/api.ts';
import { linkProps } from '../app/router.ts';
import type { Cohort } from './cohort-summary.ts';
import type { SessionRow } from './wire.ts';

/** Create a teaching cohort and open its stored results. */
export function CohortControls({ cohorts, onCreated }: { cohorts: Cohort[]; onCreated: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return <section className="fac-panel" aria-label="Cohorts">
    <h2 className="display">Cohorts</h2>
    <form onSubmit={async (event) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      try {
        await api('/api/cohorts', { body: { name: name.trim() } });
        setName('');
        onCreated();
      } catch (cause) {
        console.warn('Cohort creation failed', cause);
        setError((cause as Error).message);
      } finally { setBusy(false); }
    }}>
      <div className="field">
        <label htmlFor="cohort-name">Cohort name</label>
        <input id="cohort-name" className="input" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
      </div>
      <button className="btn btn-small" disabled={busy || !name.trim()}>Create cohort</button>
      {error && <p className="form-error" role="alert">{error}</p>}
    </form>
    <ul>{cohorts.map((cohort) => <li key={cohort.id}><a {...linkProps(`/control/cohorts/${cohort.id}`)}>{cohort.name}</a></li>)}</ul>
  </section>;
}

/** Assign or clear a session's teaching cohort. */
export function CohortAssignment({ session, cohorts, onAssigned }: { session: SessionRow; cohorts: Cohort[]; onAssigned: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return <div>
    <select className="input" aria-label={`Cohort for ${session.name}`} data-testid={`cohort-${session.id}`} disabled={busy}
      value={session.cohortId ?? ''} onChange={async (event) => {
        setBusy(true);
        setError(null);
        try {
          await api(`/api/sessions/${session.id}`, { method: 'PATCH', body: { cohort_id: event.target.value || null } });
          onAssigned();
        } catch (cause) {
          console.warn(`Cohort assignment failed for ${session.id}`, cause);
          setError((cause as Error).message);
        } finally { setBusy(false); }
      }}>
      <option value="">No cohort</option>
      {cohorts.map((cohort) => <option key={cohort.id} value={cohort.id}>{cohort.name}</option>)}
    </select>
    {session.cohortId && <a {...linkProps(`/control/cohorts/${session.cohortId}?session=${session.id}`)}>Compare cohort</a>}
    {error && <p className="form-error" role="alert">{error}</p>}
  </div>;
}
